import { Col, DatePicker, Form, Input, Radio, Row, Select } from 'antd'
import { ReactElement, ReactNode } from 'react'

import {
  DeathCause,
  LeftCountryType,
  MaritalStatus,
  VitalStatus
} from 'src/api/requests'
import { CountrySelect } from 'src/components'
import { dayMonthYearFormatWithDot, disabledFutureDate } from 'src/libs'
import { useLocales } from 'src/locales'

type Field =
  | 'patronymic'
  | 'maritalStatus'
  | 'vitalStatus'
  | 'deathDate'
  | 'deathCause'
  | 'leftCountry'
  | 'leftCountryDate'
  | 'leftCountryType'
  | 'arrivedFromAbroadDate'
  | 'arrivedFromCountry'

type Props = {
  /** Path of the demographics object in the form, e.g. `['demographics']`. */
  namePrefix: (string | number)[]
  /** Form 058-1/e requires the marital and vital status. */
  isRequired?: boolean
  patronymicDisabled?: boolean
  /** Renders a field label, e.g. with the form number; defaults to the plain label. */
  renderLabel?: (field: Field, label: string) => ReactNode
  /** Wraps the input of each field, e.g. to show the value as text in a read-only form; defaults to the input. */
  wrapControl?: (control: ReactElement) => ReactElement
  /**
   * Only the vital status (12 / 12.1) and leaving the country (13), e.g. for the yearly ECDC update of
   * Form 058-1/e; defaults to all fields.
   */
  onlyVitalAndLeftCountry?: boolean
}

const HOME_COUNTRY = 'md'

/**
 * Civil and migration data of a person (Form 058-1/e field 6 patronymic, 11–14), with the rules of the API:
 * the date and cause of death are required for a deceased person (and only then); "left the country" = Da
 * requires the type of leaving (the date is optional); the arrival date and the country of origin (not
 * Moldova) go together.
 */
export const PersonDemographicsFields = ({
  namePrefix,
  isRequired = false,
  patronymicDisabled = false,
  renderLabel = (_, label) => label,
  wrapControl = (control) => control,
  onlyVitalAndLeftCountry = false
}: Props) => {
  const { t } = useLocales()
  const form = Form.useFormInstance()

  const name = (field: Field) => [...namePrefix, field]
  const label = (field: Field) => renderLabel(field, t(`demographics.${field}`))

  const vitalStatus = Form.useWatch(name('vitalStatus'), form)
  const isDeceased = vitalStatus === VitalStatus.DECEASED

  const hasLeftCountry = Form.useWatch(name('leftCountry'), form) === true
  const arrivedFromAbroadDate = Form.useWatch(
    name('arrivedFromAbroadDate'),
    form
  )
  const arrivedFromCountry = Form.useWatch(name('arrivedFromCountry'), form)

  // 13: the type (required) and date (optional) of leaving exist only for "Da".
  const onLeftCountryChange = (value: boolean) => {
    if (value) return
    form.setFieldValue(name('leftCountryType'), undefined)
    form.setFieldValue(name('leftCountryDate'), undefined)
  }

  // The API rejects death data for a living person.
  const onVitalStatusChange = (value: VitalStatus) => {
    if (value === VitalStatus.DECEASED) return
    form.setFieldValue(name('deathDate'), undefined)
    form.setFieldValue(name('deathCause'), undefined)
  }

  return (
    <Row gutter={[24, 24]}>
      {!onlyVitalAndLeftCountry && (
        <>
          <Col xs={24} lg={8}>
            <Form.Item name={name('patronymic')} label={label('patronymic')}>
              {wrapControl(
                <Input
                  maxLength={250}
                  disabled={patronymicDisabled || undefined}
                  placeholder={t('demographics.patronymic')}
                />
              )}
            </Form.Item>
          </Col>

          <Col xs={24} lg={16}>
            <Form.Item
              name={name('maritalStatus')}
              label={label('maritalStatus')}
              rules={[{ required: isRequired }]}
            >
              {wrapControl(
                <Radio.Group>
                  {Object.values(MaritalStatus).map((value) => (
                    <Radio key={value} value={value}>
                      {t(`demographics.marital_status.${value}`)}
                    </Radio>
                  ))}
                </Radio.Group>
              )}
            </Form.Item>
          </Col>
        </>
      )}

      <Col xs={24} lg={8}>
        <Form.Item
          name={name('vitalStatus')}
          label={label('vitalStatus')}
          rules={[{ required: isRequired }]}
        >
          {wrapControl(
            <Radio.Group onChange={(e) => onVitalStatusChange(e.target.value)}>
              {Object.values(VitalStatus).map((value) => (
                <Radio key={value} value={value}>
                  {t(`demographics.vital_status.${value}`)}
                </Radio>
              ))}
            </Radio.Group>
          )}
        </Form.Item>
      </Col>

      {isDeceased && (
        <>
          <Col xs={24} lg={8}>
            <Form.Item
              name={name('deathDate')}
              label={label('deathDate')}
              rules={[{ required: true }]}
            >
              {wrapControl(
                <DatePicker
                  className="full-width"
                  format={dayMonthYearFormatWithDot}
                  disabledDate={disabledFutureDate}
                  placeholder={t('demographics.deathDate')}
                />
              )}
            </Form.Item>
          </Col>

          <Col xs={24} lg={8}>
            <Form.Item
              name={name('deathCause')}
              label={label('deathCause')}
              rules={[{ required: true }]}
            >
              {wrapControl(
                <Select
                  allowClear
                  placeholder={t('demographics.deathCause')}
                  options={Object.values(DeathCause).map((value) => ({
                    value,
                    label: t(`demographics.death_cause.${value}`)
                  }))}
                />
              )}
            </Form.Item>
          </Col>
        </>
      )}

      {!isDeceased && <Col xs={0} lg={16} />}

      <Col xs={24} lg={8}>
        <Form.Item name={name('leftCountry')} label={label('leftCountry')}>
          {wrapControl(
            <Radio.Group onChange={(e) => onLeftCountryChange(e.target.value)}>
              <Radio value={true}>{t('demographics.yes')}</Radio>
              <Radio value={false}>{t('demographics.no')}</Radio>
            </Radio.Group>
          )}
        </Form.Item>
      </Col>

      {hasLeftCountry ? (
        <>
          <Col xs={24} lg={8}>
            <Form.Item
              name={name('leftCountryType')}
              label={label('leftCountryType')}
              rules={[
                {
                  required: true,
                  message: t('demographics.left_country_type_required')
                }
              ]}
            >
              {wrapControl(
                <Radio.Group>
                  {Object.values(LeftCountryType).map((value) => (
                    <Radio key={value} value={value}>
                      {t(`demographics.left_country_type.${value}`)}
                    </Radio>
                  ))}
                </Radio.Group>
              )}
            </Form.Item>
          </Col>

          <Col xs={24} lg={8}>
            <Form.Item
              name={name('leftCountryDate')}
              label={label('leftCountryDate')}
            >
              {wrapControl(
                <DatePicker
                  className="full-width"
                  format={dayMonthYearFormatWithDot}
                  disabledDate={disabledFutureDate}
                  placeholder={t('demographics.leftCountryDate')}
                />
              )}
            </Form.Item>
          </Col>
        </>
      ) : (
        <Col xs={0} lg={16} />
      )}

      {!onlyVitalAndLeftCountry && (
        <>
          <Col xs={24} lg={8}>
            <Form.Item
              name={name('arrivedFromAbroadDate')}
              label={label('arrivedFromAbroadDate')}
              rules={[
                {
                  required: !!arrivedFromCountry,
                  message: t('demographics.arrived_together')
                }
              ]}
            >
              {wrapControl(
                <DatePicker
                  className="full-width"
                  format={dayMonthYearFormatWithDot}
                  disabledDate={disabledFutureDate}
                  placeholder={t('demographics.arrivedFromAbroadDate')}
                />
              )}
            </Form.Item>
          </Col>

          <Col xs={24} lg={8}>
            <Form.Item
              name={name('arrivedFromCountry')}
              label={label('arrivedFromCountry')}
              rules={[
                {
                  required: !!arrivedFromAbroadDate,
                  message: t('demographics.arrived_together')
                },
                {
                  validator: (_, value) =>
                    value === HOME_COUNTRY
                      ? Promise.reject(t('demographics.arrived_not_home'))
                      : Promise.resolve()
                }
              ]}
            >
              {wrapControl(
                <CountrySelect
                  allowClear
                  placeholder={t('demographics.arrivedFromCountry')}
                />
              )}
            </Form.Item>
          </Col>
        </>
      )}
    </Row>
  )
}
